import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';
import { useColors } from '@/contexts/ThemeContext';

// ============================================================================
// TYPES
// ============================================================================

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Label text displayed above textarea */
  label?: string;
  /** Error message - displays in red below textarea */
  error?: string;
  /** Helper text - displays in gray below textarea */
  helperText?: string;
  /** Full width textarea */
  fullWidth?: boolean;
  /** Show required asterisk */
  required?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Textarea Component
 * 
 * Professional textarea field with:
 * - Label support
 * - Error states
 * - Helper text
 * - Required indicator
 * - Professional color system
 * - Consistent styling
 * 
 * @example
 * // Basic textarea
 * <Textarea label="Descripción" placeholder="Escribe aquí..." />
 * 
 * // With error
 * <Textarea label="Notas" error="Campo requerido" />
 * 
 * // Required field
 * <Textarea label="Comentario" required />
 */
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ 
    className, 
    label, 
    error, 
    helperText,
    id,
    fullWidth = true,
    disabled,
    required,
    rows = 3,
    ...props 
  }, ref) => {
    const colors = useColors();
    const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const hasError = !!error;

    return (
      <div className={cn('space-y-1.5', fullWidth && 'w-full')}>
        {/* Label */}
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium"
            style={{
              color: colors.foreground.primary,
              fontFamily: 'var(--font-system, "Area", sans-serif)',
            }}
          >
            {label}
            {required && (
              <span style={{ color: colors.status.error, marginLeft: '4px' }}>*</span>
            )}
          </label>
        )}

        {/* Textarea */}
        <textarea
          ref={ref}
          id={textareaId}
          disabled={disabled}
          rows={rows}
          className={cn(
            'flex w-full rounded-lg px-3 py-2 text-sm',
            'transition-all duration-200',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'resize-none',
            className
          )}
          style={{
            backgroundColor: colors.background.primary,
            color: colors.foreground.primary,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: hasError 
              ? colors.status.error 
              : colors.border.primary,
            fontFamily: 'var(--font-system, "Area", sans-serif)',
          }}
          {...props}
        />

        {/* Error message */}
        {error && (
          <p 
            className="text-xs font-medium"
            style={{
              color: colors.status.error,
              fontFamily: 'var(--font-system, "Area", sans-serif)',
            }}
          >
            {error}
          </p>
        )}

        {/* Helper text */}
        {helperText && !error && (
          <p 
            className="text-xs"
            style={{
              color: colors.foreground.secondary,
              fontFamily: 'var(--font-system, "Area", sans-serif)',
            }}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };
export default Textarea;
